import React, { useState } from 'react';
import { Row, Col, Table, Button, Modal, Form, ButtonGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../shared/CurrencyContext';
import Select from '../../components/form/AppSelect';
import NumberInput from '../../components/form/NumberInput';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { getContrastColor, COLOR_SUCCESS, COLOR_SECONDARY, COLOR_WARNING } from '../../shared/colorUtils';
import { useConfirm } from '../../shared/ConfirmContext';
import { IconPlus } from '@tabler/icons-react';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import AppButton from '../../components/ui/AppButton';
import { CenteredLoader } from '../../components/LoadingSpinner';

const Tariffs = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    tariff_type: null,
    amount: '',
    amount_vat: '',
    valid_from: '',
    valid_to: '',
    priority: 0,
    is_active: true,
    is_promotional: false,
    note: '',
    description: ''
  });

  const { data: tariffs = [], isLoading } = useQuery({
    queryKey: ['tariffs'],
    queryFn: () => api.get('settings/api/tariffs/').then(d => Array.isArray(d) ? d : []),
  });

  const { data: tariffTypes = [] } = useQuery({
    queryKey: ['tariff-types-active'],
    queryFn: () => api.get('settings/api/tariff-types/active/').then(d => Array.isArray(d) ? d : []),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tariffs'] });

  const saveMutation = useMutation({
    mutationFn: (payload) => editingTariff
      ? api.put(`settings/api/tariffs/${editingTariff.id}/`, payload)
      : api.post('settings/api/tariffs/', payload),
    onSuccess: () => { invalidate(); handleCloseModal(); },
    onError: (err) => setError(err.message || 'Failed to save tariff'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`settings/api/tariffs/${id}/`),
    onSuccess: invalidate,
    onError: (err) => setError(err.message || 'Failed to delete tariff'),
  });

  const duplicateMutation = useMutation({
    mutationFn: (id) => api.post(`settings/api/tariffs/${id}/duplicate/`),
    onSuccess: invalidate,
    onError: (err) => setError(err.message || 'Failed to duplicate tariff'),
  });

  const loading = isLoading;

  const handleShowModal = (tariff = null) => {
    if (tariff) {
      setEditingTariff(tariff);
      setFormData({
        name: tariff.name,
        tariff_type: tariff.tariff_type.id,
        amount: tariff.amount,
        amount_vat: tariff.amount_vat || '',
        valid_from: tariff.valid_from || '',
        valid_to: tariff.valid_to || '',
        priority: tariff.priority,
        is_active: tariff.is_active,
        is_promotional: tariff.is_promotional,
        note: tariff.note || '',
        description: tariff.description || ''
      });
    } else {
      setEditingTariff(null);
      setFormData({
        name: '',
        tariff_type: null,
        amount: '',
        amount_vat: '',
        valid_from: '',
        valid_to: '',
        priority: 0,
        is_active: true,
        is_promotional: false,
        note: '',
        description: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTariff(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  const handleDelete = async (id) => {
    if (!await confirm(t('settings.tariffs.confirmDelete') || 'Are you sure you want to delete this tariff?')) return;
    deleteMutation.mutate(id);
  };

  const handleDuplicate = (id) => {
    duplicateMutation.mutate(id);
  };

  const filteredTariffs = tariffs.filter(tariff => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'active') return tariff.is_active;
    if (filterStatus === 'inactive') return !tariff.is_active;
    if (filterStatus === 'promotional') return tariff.is_promotional;
    return true;
  });

  const tariffTypeOptions = tariffTypes.map(type => ({
    value: type.id,
    label: `${type.name} (${type.unit})`
  }));

  if (loading) {
    return (
      <PageShell pretitle={t('settings.overview')} title={t('settings.tariffs.title')}>
        <CenteredLoader />
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('settings.overview')}
      title={t('settings.tariffs.title')}
      actions={
        <AppButton variant="primary" icon={<IconPlus size={16} />} onClick={() => handleShowModal()}>
          {t('settings.tariffs.add')}
        </AppButton>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}

      <ButtonGroup className="mb-3">
        <Button variant={filterStatus === 'all' ? 'primary' : 'outline-primary'} onClick={() => setFilterStatus('all')}>{t('settings.tariffs.filters.all')}</Button>
        <Button variant={filterStatus === 'active' ? 'primary' : 'outline-primary'} onClick={() => setFilterStatus('active')}>{t('settings.tariffs.filters.active')}</Button>
        <Button variant={filterStatus === 'inactive' ? 'primary' : 'outline-primary'} onClick={() => setFilterStatus('inactive')}>{t('settings.tariffs.filters.inactive')}</Button>
        <Button variant={filterStatus === 'promotional' ? 'primary' : 'outline-primary'} onClick={() => setFilterStatus('promotional')}>{t('settings.tariffs.filters.promotional')}</Button>
      </ButtonGroup>

      <PageSection bodyClassName="p-0">
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>{t('settings.tariffs.name')}</th>
                        <th>{t('settings.tariffs.type')}</th>
                        <th>{t('settings.tariffs.amount')}</th>
                        <th>{t('settings.tariffs.validFrom')}</th>
                        <th>{t('settings.tariffs.validTo')}</th>
                        <th>{t('settings.tariffs.priority')}</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTariffs.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center text-muted">
                            No tariffs found
                          </td>
                        </tr>
                      ) : (
                        filteredTariffs.map(tariff => (
                          <tr key={tariff.id}>
                            <td>{tariff.name}</td>
                            <td>
                              <span className="badge" style={{ backgroundColor: tariff.tariff_type.color, color: getContrastColor(tariff.tariff_type.color) }}>
                                {tariff.tariff_type.name}
                              </span>
                            </td>
                            <td>
                              {formatCurrency(tariff.amount)}
                              {tariff.amount_vat && (
                                <span className="text-muted"> ({formatCurrency(tariff.amount_vat)} incl. VAT)</span>
                              )}
                            </td>
                            <td>{tariff.valid_from || '-'}</td>
                            <td>{tariff.valid_to || '-'}</td>
                            <td>{tariff.priority}</td>
                            <td>
                              {tariff.is_active ? (
                                <span className="badge" style={{ backgroundColor: COLOR_SUCCESS, color: getContrastColor(COLOR_SUCCESS) }}>{t('settings.tariffs.filters.active')}</span>
                              ) : (
                                <span className="badge" style={{ backgroundColor: COLOR_SECONDARY, color: getContrastColor(COLOR_SECONDARY) }}>{t('settings.tariffs.filters.inactive')}</span>
                              )}
                              {tariff.is_promotional && (
                                <span className="badge ms-1" style={{ backgroundColor: COLOR_WARNING, color: getContrastColor(COLOR_WARNING) }}>
                                  {t('settings.tariffs.filters.promotional')}
                                </span>
                              )}
                            </td>
                            <td>
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => handleShowModal(tariff)}
                                title="Edit"
                              >
                                <i className="bi bi-pencil"></i>
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => handleDuplicate(tariff.id)}
                                title="Duplicate"
                              >
                                <i className="bi bi-files"></i>
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                className="text-danger"
                                onClick={() => handleDelete(tariff.id)}
                                title="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
      </PageSection>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingTariff ? t('settings.tariffs.edit') : t('settings.tariffs.add')}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('settings.tariffs.name')} *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('settings.tariffs.type')} *</Form.Label>
                  <Select
                    value={tariffTypeOptions.find(opt => opt.value === formData.tariff_type)}
                    onChange={(option) => setFormData({ ...formData, tariff_type: option?.value })}
                    options={tariffTypeOptions}
                    isClearable
                    placeholder={t('settings.tariffs.type')}
                    required
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('settings.tariffs.priority')}</Form.Label>
                  <NumberInput
                    step="any"
                    value={formData.priority}
                    onChange={(priority) => setFormData({ ...formData, priority })}
                  />
                  <Form.Text className="text-muted">
                    Higher priority = preferred tariff
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('settings.tariffs.amount')} *</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('settings.tariffs.amountVat')}</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={formData.amount_vat}
                    onChange={(e) => setFormData({ ...formData, amount_vat: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('settings.tariffs.validFrom')}</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('settings.tariffs.validTo')}</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.valid_to}
                    onChange={(e) => setFormData({ ...formData, valid_to: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>{t('settings.tariffs.description')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Public description for customers"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('settings.tariffs.note')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="Internal note"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label={t('settings.tariffs.isActive')}
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label={t('settings.tariffs.isPromotional')}
                  checked={formData.is_promotional}
                  onChange={(e) => setFormData({ ...formData, is_promotional: e.target.checked })}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" type="submit">
              {editingTariff ? t('common.save') : t('common.add')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </PageShell>
  );
};

export default Tariffs;
